import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2, RotateCcw } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PAGE_SEO, breadcrumbLd, faqLd } from "@/lib/seo";
import { useBrief } from "@/context/BriefContext";
import { submitScorecard, getScorecardConfig, getScorecardReport } from "@/lib/api";
import { getUTM, track } from "@/lib/analytics";
import {
  DEFAULT_SCORECARD,
  SECTORS,
  SHORT_ANSWERS,
  bandForScore,
  recommendedNextStep,
  lowestThree,
} from "@/data/scorecard";

const COPPER_TEXT = "#8c5740"; // AA copper-deep for small text
const COPPER_ACCENT = "#a0644b"; // decorative accent (rules, large)

// Intro-only supporting content.
const SCORECARD_FAQS = [
  {
    q: "What does the Readiness Scorecard measure?",
    a: "Ten questions, ten of the fifty we work through in a Sprint, across strategy, data, governance, and delivery. You get a personalized read on where you stand and what to build, buy, or fix next.",
  },
  {
    q: "How long does it take?",
    a: "A few minutes. Ten questions, plus two optional short answers that sharpen the result. No call required.",
  },
  {
    q: "What do I get at the end?",
    a: "A score, a band that explains what it means, your three biggest opportunities, and a recommended next step. The full read lands in your inbox when you add your email.",
  },
  {
    q: "Is it really free?",
    a: "Yes. The Scorecard is free and there is no obligation. It is the smallest, lowest-risk way to get a senior read on where you stand.",
  },
];

function ProgressBar({ step, total, reduce }) {
  const pct = Math.round((step / total) * 100);
  return (
    <div data-testid="scorecard-progress">
      <div className="flex items-center justify-between">
        <p className="exec-eyebrow">Readiness Scorecard</p>
        <p className="text-[11px] uppercase tracking-[0.16em] font-normal text-[color:var(--grey)]" data-testid="scorecard-progress-label">
          {step} of {total}
        </p>
      </div>
      <div className="mt-3 h-1 w-full bg-[color:var(--rule)] overflow-hidden" role="progressbar"
           aria-valuemin={0} aria-valuemax={total} aria-valuenow={step} aria-label="Scorecard progress">
        <motion.div
          className="h-full"
          style={{ background: COPPER_ACCENT }}
          initial={false}
          animate={{ width: `${pct}%` }}
          transition={{ duration: reduce ? 0 : 0.3, ease: "easeOut" }}
        />
      </div>
    </div>
  );
}

function OptionButton({ testid, selected, index, label, onClick }) {
  return (
    <button
      type="button"
      data-testid={testid}
      data-selected={selected}
      aria-pressed={selected}
      onClick={onClick}
      className="exec-option w-full text-left px-5 py-4 flex items-center gap-4"
    >
      <span
        className="flex-shrink-0 grid place-items-center h-8 w-8 rounded-full border text-sm font-normal"
        style={{
          borderColor: selected ? COPPER_ACCENT : "#e6e0d6",
          color: selected ? COPPER_TEXT : "#8a837a",
          background: selected ? "#f3ece4" : "transparent",
        }}
        aria-hidden="true"
      >
        {selected ? <Check className="h-4 w-4" /> : index}
      </span>
      <span className="exec-serif text-[17px] sm:text-[18px] text-[color:var(--ink)] leading-snug">{label}</span>
    </button>
  );
}

const textareaClass =
  "mt-2 w-full rounded-[2px] border border-[color:var(--rule)] bg-transparent px-4 py-3 exec-serif text-[16px] " +
  "text-[color:var(--ink)] placeholder:text-[color:var(--grey)] focus:outline-none focus:border-[color:var(--copper)] transition-colors resize-none";

export default function Scorecard() {
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const { openBrief } = useBrief();

  // Live, admin-editable config (falls back to the bundled default).
  const [config, setConfig] = useState(DEFAULT_SCORECARD);
  useEffect(() => {
    getScorecardConfig()
      .then((d) => {
        if (d && Array.isArray(d.questions) && d.questions.length) setConfig(d);
      })
      .catch(() => {});
  }, []);

  const SEGMENTS = config.segments || DEFAULT_SCORECARD.segments;
  const QUESTIONS = config.questions || DEFAULT_SCORECARD.questions;
  const BANDS = config.bands || DEFAULT_SCORECARD.bands;
  const intro = config.intro || DEFAULT_SCORECARD.intro;
  const MAX_SCORE = QUESTIONS.length * 3;

  const total = QUESTIONS.length; // 10
  // step: 0 = segment | "sector" | 1..total = questions | "short" | "result"
  const [step, setStep] = useState(0);
  const [segment, setSegment] = useState(null);
  const [sector, setSector] = useState(null);
  const [sectorOther, setSectorOther] = useState("");
  const [answers, setAnswers] = useState({});
  const [short, setShort] = useState({ decision: "", ready_definition: "" });

  // AI report state
  const [report, setReport] = useState("");
  const [reportState, setReportState] = useState("idle"); // idle | loading | done | error

  // Email gate state
  const [email, setEmail] = useState("");
  const [gateState, setGateState] = useState("idle"); // idle | loading | done | error
  const [gateError, setGateError] = useState("");

  const enter = {
    initial: { opacity: 0, x: reduce ? 0 : 24 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: reduce ? 0 : -24 },
    transition: { duration: reduce ? 0 : 0.22, ease: "easeOut" },
  };

  const currentQ = typeof step === "number" && step >= 1 && step <= total ? QUESTIONS[step - 1] : null;

  const chooseSegment = (val) => {
    setSegment(val);
    track("scorecard_start", { segment: val });
    setStep("sector");
  };

  const chooseSector = (val) => {
    setSector(val);
    track("scorecard_sector", { sector: val });
    if (val !== "other") setStep(1);
  };

  const choose = (q, value) => {
    const next = { ...answers, [q.key]: value };
    setAnswers(next);
    if (step < total) setStep(step + 1);
    else setStep("short");
  };

  const goToResult = () => {
    track("scorecard_complete", { segment, sector, score });
    setReportState("idle"); // (re)trigger AI report generation
    setStep("result");
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  };

  const goBack = () => {
    if (step === "result") setStep("short");
    else if (step === "short") setStep(total);
    else if (step === 1) setStep("sector");
    else if (step === "sector") setStep(0);
    else if (typeof step === "number" && step > 1) setStep(step - 1);
  };

  // ---- Result computations ----
  const score = Object.values(answers).reduce((a, b) => a + b, 0);
  const band = bandForScore(score, BANDS);
  const appetite = answers.appetite ?? null;
  const rec = recommendedNextStep(segment, band.key, appetite);
  const low3 = lowestThree(answers, QUESTIONS);

  const ctaLabel = rec.referral
    ? "Explore the referral program"
    : rec.softened
    ? "Request a conversation"
    : rec.publicSector
    ? "Request the Public Sector brief"
    : `Request the brief on ${rec.offer.label}`;

  // ---- AI personalized report ----
  useEffect(() => {
    if (step !== "result" || reportState !== "idle") return;
    setReportState("loading");
    getScorecardReport({
      segment,
      sector: sector || "",
      sector_other: sectorOther,
      band: band.key,
      band_label: band.label,
      score,
      max_score: MAX_SCORE,
      lowest_three: low3.map((q) => ({ category: q.category, tag: q.tag, score: q.score })),
      decision: short.decision,
      ready_definition: short.ready_definition,
      recommended_label: rec.referral ? "the referral program" : rec.offer.label,
      softened: rec.softened,
    })
      .then((d) => {
        setReport((d && d.report) || "");
        setReportState("done");
      })
      .catch(() => {
        setReport("");
        setReportState("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const reportParagraphs = report ? report.split(/\n{2,}/).filter(Boolean) : [];

  const handleCTA = () => {
    if (rec.referral) {
      track("scorecard_cta", { type: "referral" });
      navigate("/refer");
      return;
    }
    track("scorecard_cta", { type: "brief", offer: rec.offer.slug, softened: rec.softened });
    openBrief(rec.offer.slug);
  };

  const submitGate = async (e) => {
    e.preventDefault();
    setGateError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setGateError("Enter a valid email address.");
      return;
    }
    setGateState("loading");
    try {
      await submitScorecard({
        email,
        segment,
        sector: sector || "",
        sector_other: sectorOther,
        band: band.key,
        score,
        lowest_three: low3.map((q) => q.key),
        recommended_offer: rec.referral ? "referral-program" : rec.offer.slug,
        decision: short.decision,
        ready_definition: short.ready_definition,
        report,
        answers,
        utm: getUTM(),
      });
      track("scorecard_email", { segment, sector, band: band.key });
      setGateState("done");
    } catch (err) {
      setGateState("error");
      setGateError(
        err?.response?.data?.detail
          ? String(err.response.data.detail)
          : "Something went wrong. Please try again."
      );
    }
  };

  return (
    <div className="exec-scope min-h-screen">
      <Seo
        {...PAGE_SEO.scorecard}
        jsonLd={[
          faqLd(SCORECARD_FAQS.map((f) => ({ q: f.q, a: f.a }))),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Scorecard", path: "/scorecard" },
          ]),
        ]}
      />
      <section className="exec-container pt-24 md:pt-28 pb-16">
        <div className="max-w-[760px] mx-auto">
          {/* Top controls */}
          <div className="flex items-center justify-between mb-8">
            {step !== 0 ? (
              <button
                type="button"
                onClick={goBack}
                data-testid="scorecard-back"
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-normal text-[color:var(--grey)] hover:text-[color:var(--ink)] transition-colors"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            ) : (
              <Link
                to="/resources"
                data-testid="scorecard-exit"
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-normal text-[color:var(--grey)] hover:text-[color:var(--ink)] transition-colors"
              >
                <ArrowLeft className="h-4 w-4" /> Resources
              </Link>
            )}
            {currentQ && (
              <span className="text-[11px] uppercase tracking-[0.16em] font-normal text-[color:var(--grey)]">
                {Math.round((step / total) * 100)}%
              </span>
            )}
          </div>

          <AnimatePresence mode="wait">
            {/* Q0 — Segment */}
            {step === 0 && (
              <motion.div key="segment" {...enter} data-testid="scorecard-segment-step">
                <p className="exec-eyebrow">Start here</p>
                <h1 className="font-light text-[clamp(28px,3.6vw,42px)] leading-[1.14] mt-4">
                  {intro.headline}
                </h1>
                <p className="exec-serif text-[clamp(17px,1.9vw,21px)] leading-[1.5] text-[color:var(--lead-ink)] mt-5 max-w-[640px]" data-testid="scorecard-intro-copy">
                  {intro.subhead}
                </p>
                <div className="mt-9 grid gap-3" role="group" aria-label="Your context">
                  {SEGMENTS.map((s, i) => (
                    <OptionButton
                      key={s.value}
                      testid={`scorecard-segment-${s.value}`}
                      selected={segment === s.value}
                      index={i + 1}
                      label={s.label}
                      onClick={() => chooseSegment(s.value)}
                    />
                  ))}
                </div>
              </motion.div>
            )}

            {/* Sector question */}
            {step === "sector" && (
              <motion.div key="sector" {...enter} data-testid="scorecard-sector-step">
                <p className="exec-eyebrow">A bit of context</p>
                <h1 className="font-light text-[clamp(24px,3vw,34px)] leading-[1.18] mt-3">
                  What sector are you in?
                </h1>
                <div className="mt-7 grid gap-3 sm:grid-cols-2" role="group" aria-label="Sector">
                  {SECTORS.map((s, i) => (
                    <OptionButton
                      key={s.value}
                      testid={`scorecard-sector-${s.value}`}
                      selected={sector === s.value}
                      index={i + 1}
                      label={s.label}
                      onClick={() => chooseSector(s.value)}
                    />
                  ))}
                </div>
                {sector === "other" && (
                  <div className="mt-6">
                    <label
                      htmlFor="scorecard-sector-other"
                      className="block text-[11px] uppercase tracking-[0.16em] font-normal text-[color:var(--ink)]"
                    >
                      Tell us your sector
                    </label>
                    <input
                      id="scorecard-sector-other"
                      data-testid="scorecard-sector-other-input"
                      value={sectorOther}
                      onChange={(e) => setSectorOther(e.target.value)}
                      placeholder="Your sector"
                      className="exec-underline mt-2"
                    />
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      data-testid="scorecard-sector-continue"
                      className="exec-btn exec-btn-primary mt-6"
                    >
                      Continue <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {/* Questions 1..10 */}
            {currentQ && (
              <motion.div key={currentQ.key} {...enter} data-testid={`scorecard-question-${currentQ.key}`}>
                <ProgressBar step={step} total={total} reduce={reduce} />
                <div className="mt-8 flex items-center gap-3">
                  <p className="exec-eyebrow">{currentQ.category}</p>
                  <span
                    className="text-[0.62rem] uppercase tracking-[0.16em] font-normal px-2 py-0.5 rounded-[2px] border"
                    style={{ color: COPPER_TEXT, borderColor: "#e6e0d6", background: "#f3ece4" }}
                  >
                    {currentQ.tag}
                  </span>
                </div>
                <h1 className="font-light text-[clamp(24px,3vw,34px)] leading-[1.18] mt-3">
                  {currentQ.prompt}
                </h1>
                <div className="mt-7 grid gap-3" role="group" aria-label={currentQ.category}>
                  {currentQ.options.map((label, value) => (
                    <OptionButton
                      key={value}
                      testid={`scorecard-option-${currentQ.key}-${value}`}
                      selected={answers[currentQ.key] === value}
                      index={value}
                      label={label}
                      onClick={() => choose(currentQ, value)}
                    />
                  ))}
                </div>
              </motion.div>
            )}

            {/* Short answers (optional) */}
            {step === "short" && (
              <motion.div key="short" {...enter} data-testid="scorecard-short-step">
                <p className="exec-eyebrow">Last step, optional</p>
                <h1 className="font-light text-[clamp(24px,3vw,34px)] leading-[1.18] mt-3">
                  Two short questions, in your words.
                </h1>
                <p className="exec-serif text-[17px] leading-[1.5] text-[color:var(--lead-ink)] mt-3 max-w-xl">
                  Optional, but they make your report read as written for you, not a template.
                </p>
                <div className="mt-7 grid gap-6">
                  {SHORT_ANSWERS.map((f) => (
                    <div key={f.key}>
                      <label
                        htmlFor={`scorecard-short-${f.key}`}
                        className="block exec-serif text-[17px] text-[color:var(--ink)]"
                      >
                        {f.label}
                      </label>
                      <textarea
                        id={`scorecard-short-${f.key}`}
                        data-testid={`scorecard-short-${f.key}`}
                        rows={2}
                        value={short[f.key]}
                        onChange={(e) => setShort((s) => ({ ...s, [f.key]: e.target.value }))}
                        placeholder={f.placeholder}
                        className={textareaClass}
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-8 flex items-center gap-6">
                  <button
                    type="button"
                    onClick={goToResult}
                    data-testid="scorecard-short-continue"
                    className="exec-btn exec-btn-primary"
                  >
                    See your result <ArrowRight className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={goToResult}
                    data-testid="scorecard-short-skip"
                    className="exec-link"
                  >
                    Skip
                  </button>
                </div>
              </motion.div>
            )}

            {/* Result */}
            {step === "result" && (
              <motion.div key="result" {...enter} data-testid="scorecard-result">
                <p className="exec-eyebrow">Your opportunity map</p>
                <div className="mt-4 flex flex-wrap items-end gap-x-5 gap-y-2">
                  <h1 className="font-extralight text-[clamp(40px,5vw,64px)] leading-none" data-testid="scorecard-band">
                    {band.label}
                  </h1>
                  <span className="text-[13px] uppercase tracking-[0.14em] font-normal text-[color:var(--grey)]" data-testid="scorecard-score">
                    {score} of {MAX_SCORE}
                  </span>
                </div>

                {/* Personalized AI report (falls back to the static band read) */}
                {reportState === "loading" ? (
                  <div className="mt-6 space-y-3" data-testid="scorecard-report-loading" aria-live="polite">
                    <div className="h-4 w-full bg-[color:var(--rule)] animate-pulse" />
                    <div className="h-4 w-[92%] bg-[color:var(--rule)] animate-pulse" />
                    <div className="h-4 w-[78%] bg-[color:var(--rule)] animate-pulse" />
                  </div>
                ) : reportParagraphs.length > 0 ? (
                  <div className="mt-6 space-y-4 max-w-2xl" data-testid="scorecard-report">
                    {reportParagraphs.map((p, i) => (
                      <p key={i} className="exec-serif text-[clamp(17px,1.9vw,20px)] leading-[1.55] text-[color:var(--ink)]">
                        {p}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="exec-serif text-[clamp(17px,1.9vw,20px)] leading-[1.55] text-[color:var(--ink)] mt-6 max-w-2xl" data-testid="scorecard-band-read">
                    {band.read}
                  </p>
                )}

                <p className="exec-serif italic text-[16px] text-[color:var(--lead-ink)] mt-5 max-w-2xl" data-testid="scorecard-ten-of-fifty">
                  {intro.ten_of_fifty}
                </p>

                {/* Three opportunities */}
                <div className="mt-12">
                  <h2 className="font-light text-[clamp(20px,2.4vw,28px)] leading-[1.18]">Your top three opportunities</h2>
                  <div className="mt-6 grid gap-px sm:grid-cols-3 bg-[color:var(--rule)] p-px" data-testid="scorecard-opportunities">
                    {low3.map((q) => (
                      <div key={q.key} className="bg-[color:var(--paper)] p-6 flex flex-col" data-testid={`scorecard-opp-${q.key}`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-[0.62rem] uppercase tracking-[0.18em] font-normal" style={{ color: COPPER_TEXT }}>
                            {q.tag}
                          </p>
                          <span className="text-[11px] tracking-[0.1em] font-normal text-[color:var(--grey)]">{q.score}/3</span>
                        </div>
                        <h3 className="font-light text-[19px] leading-[1.2] mt-3">{q.category}</h3>
                        <p className="exec-serif text-[15.5px] leading-[1.5] text-[color:var(--lead-ink)] mt-2">{q.recommendation}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recommended next step */}
                <div className="mt-12 exec-card p-7 sm:p-9" style={{ borderLeft: `3px solid ${COPPER_ACCENT}` }}>
                  <p className="exec-eyebrow">Recommended next step</p>
                  {rec.referral ? (
                    <p className="exec-serif text-[clamp(17px,1.9vw,20px)] leading-[1.55] text-[color:var(--ink)] mt-3 max-w-2xl">
                      You advise the people we work with. The most useful next step is our referral
                      program: send the brief to the right operator and we&rsquo;ll take it from there.
                    </p>
                  ) : (
                    <p className="exec-serif text-[clamp(17px,1.9vw,20px)] leading-[1.55] text-[color:var(--ink)] mt-3 max-w-2xl">
                      {rec.softened
                        ? "Appetite for change is the blocker right now, not capability. Start with a conversation before a Sprint, so leadership is aligned before the work begins."
                        : `Based on where you stand, the ${rec.offer.label} is the most direct way to turn this map into a plan.`}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={handleCTA}
                    data-testid="scorecard-cta"
                    className="exec-btn exec-btn-primary mt-7"
                  >
                    {ctaLabel} <ArrowRight className="h-4 w-4" />
                  </button>
                  {rec.publicSector && (
                    <Link
                      to="/public-sector"
                      data-testid="scorecard-public-sector-link"
                      className="exec-link block mt-5"
                      onClick={() => track("scorecard_cta", { type: "public_sector_page" })}
                    >
                      Or explore our public-sector work
                    </Link>
                  )}
                </div>

                {/* Email gate */}
                <div className="mt-10 border-t border-[color:var(--rule)] pt-9">
                  {gateState === "done" ? (
                    <div className="flex items-start gap-3" data-testid="scorecard-gate-success">
                      <span className="grid place-items-center h-9 w-9 rounded-full flex-shrink-0"
                            style={{ border: `1px solid ${COPPER_ACCENT}`, color: COPPER_TEXT }}>
                        <Check className="h-5 w-5" />
                      </span>
                      <div>
                        <h2 className="font-light text-[22px]">Your map is on its way.</h2>
                        <p className="exec-serif text-[17px] text-[color:var(--lead-ink)] mt-1">
                          Check your inbox for the full opportunity map as a PDF.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={submitGate} data-testid="scorecard-gate-form" noValidate>
                      <h2 className="font-light text-[clamp(20px,2.4vw,28px)] leading-[1.18]">Get the full map as a PDF.</h2>
                      <p className="exec-serif text-[17px] leading-[1.5] text-[color:var(--lead-ink)] mt-2 max-w-xl">
                        We&rsquo;ll email your map and the recommendations. One email, nothing more.
                      </p>
                      <div className="mt-6 flex flex-col sm:flex-row sm:items-end gap-4">
                        <div className="flex-1">
                          <label
                            htmlFor="scorecard-email"
                            className="block text-[11px] uppercase tracking-[0.16em] font-normal text-[color:var(--ink)]"
                          >
                            Email
                          </label>
                          <input
                            id="scorecard-email"
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@company.com"
                            className="exec-underline"
                            data-testid="scorecard-email-input"
                            aria-invalid={!!gateError}
                            aria-describedby={gateError ? "scorecard-email-error" : undefined}
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={gateState === "loading"}
                          data-testid="scorecard-email-submit"
                          className="exec-btn exec-btn-primary"
                        >
                          {gateState === "loading" ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" /> Sending
                            </>
                          ) : (
                            "Email me the map"
                          )}
                        </button>
                      </div>
                      {gateError && (
                        <p id="scorecard-email-error" className="exec-serif text-[16px] mt-2" style={{ color: "#9a3412" }} role="alert">
                          {gateError}
                        </p>
                      )}
                    </form>
                  )}
                </div>

                {/* Retake */}
                <div className="mt-10">
                  <button
                    type="button"
                    data-testid="scorecard-retake"
                    onClick={() => {
                      setAnswers({});
                      setSegment(null);
                      setSector(null);
                      setSectorOther("");
                      setShort({ decision: "", ready_definition: "" });
                      setReport("");
                      setReportState("idle");
                      setStep(0);
                      setGateState("idle");
                      setEmail("");
                    }}
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-normal text-[color:var(--grey)] hover:text-[color:var(--ink)] transition-colors"
                  >
                    <RotateCcw className="h-4 w-4" /> Retake the scorecard
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Intro-only supporting content (helps SEO + first-time context) */}
      {step === 0 && (
        <section className="exec-container pb-20">
          <div className="max-w-[760px] mx-auto" data-testid="scorecard-about">
            <div className="exec-rule" />
            <div className="mt-14">
              <p className="exec-eyebrow">What it measures</p>
              <h2 className="font-light text-[clamp(24px,3vw,34px)] leading-[1.18] mt-4">
                A senior read on where you stand, in minutes.
              </h2>
              <p className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-4 max-w-[640px]">
                The Readiness Scorecard distills ten of the fifty questions we work through in a
                Sprint, across four dimensions: strategy, data, governance, and delivery. You answer
                ten questions, add two optional short answers if you want a sharper result, and get a
                personalized map of what to build, buy, or fix next. No call required, and the full
                read lands in your inbox.
              </p>
            </div>

            <div className="mt-14">
              <p className="exec-eyebrow">Common questions</p>
              <dl className="mt-6 divide-y divide-[color:var(--rule)] border-t border-[color:var(--rule)]">
                {SCORECARD_FAQS.map((f) => (
                  <div key={f.q} className="py-6" data-testid="scorecard-faq-item">
                    <dt className="font-light text-[19px] sm:text-[21px] leading-[1.25]">{f.q}</dt>
                    <dd className="exec-serif text-[16.5px] leading-[1.55] text-[color:var(--lead-ink)] mt-2.5">{f.a}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
